import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, MapPin, AlertTriangle } from "lucide-react";

const empty = () => ({ name: "", latitude: "", longitude: "", radius_m: 150, active: true });

export default function LocationsPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = () => api.get("/hr/attendance/locations").then(r => setRows(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const openEdit = (l) => { setEditing(l); setForm({ name: l.name, latitude: l.latitude, longitude: l.longitude, radius_m: l.radius_m, active: l.active }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const body = { ...form, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude), radius_m: parseFloat(form.radius_m) };
    if (Number.isNaN(body.latitude) || Number.isNaN(body.longitude)) return toast.error("Latitude and longitude are required");
    try {
      if (editing) await api.put(`/hr/attendance/locations/${editing.id}`, body);
      else await api.post("/hr/attendance/locations", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete location?")) return;
    try { await api.delete(`/hr/attendance/locations/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const noActive = rows.filter(r => r.active).length === 0;

  return (
    <div data-testid="att-locations-panel">
      {noActive && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-3 flex items-start gap-2 text-sm text-amber-800" data-testid="att-loc-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>No active Attendance Location is configured. Any policy that requires location verification will fail every punch (staff must use the exception flow). Add and activate a real office/warehouse location before enabling location checks in production.</span>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Geofences — a punch passes location if it is within the radius of an active location.</p>
        <Button data-testid="att-loc-new" onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />Location</Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {rows.map(l => (
          <div key={l.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`att-loc-${l.id}`}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-600" />{l.name}</h4>
              <span className="flex gap-1">
                <button onClick={() => openEdit(l)} data-testid={`att-loc-edit-${l.id}`}><Pencil className="w-4 h-4 text-slate-400" /></button>
                <button onClick={() => del(l.id)}><Trash2 className="w-4 h-4 text-rose-500" /></button>
              </span>
            </div>
            <div className="text-xs text-slate-500 font-mono">{l.latitude}, {l.longitude}</div>
            <div className="flex items-center gap-2 mt-1.5">
              <Badge variant="outline">radius {l.radius_m}m</Badge>
              <Badge variant="outline" className={l.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400"}>{l.active ? "Active" : "Inactive"}</Badge>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-2 rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-slate-400 text-sm">No locations yet</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="att-loc-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit location" : "New location"}</DialogTitle>
            <DialogDescription>A geofence for attendance. Punches within the radius of an active location pass the location check.</DialogDescription></DialogHeader>
          <div><Label>Location Name *</Label><Input data-testid="att-loc-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="FASFONE Office" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Latitude *</Label><Input data-testid="att-loc-lat" value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} placeholder="3.1390" /></div>
            <div><Label>Longitude *</Label><Input data-testid="att-loc-lng" value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} placeholder="101.6869" /></div>
          </div>
          <div><Label>Allowed Radius (metres)</Label><Input type="number" data-testid="att-loc-radius" value={form.radius_m} onChange={e => setForm({ ...form, radius_m: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} data-testid="att-loc-active" />Active</label>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="att-loc-save" className="bg-slate-900">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
